import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { styled, Theme, useMediaQuery } from "@mui/material";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_TableOptions,
} from "material-react-table";
import * as React from "react";
import { JSX, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router";
import Guid from "_shared/guids/guid";
import AdministratorInvitation from "legal-entities/invitations/administrator-invitations/administrator-invitation";
import { useSession } from "users/__session/session-context";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSelector } from "react-redux";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { 
  useGetAdminInvitationsForLegalEntityQuery
} from "legal-entities/invitations/administrator-invitations/_api/administrator-invitation-api";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

type AdministratorInvitationsTableProps = {
  renderTopToolbarCustomActions: (props: {
    table: TableInstance<AdministratorInvitation>;
    additional?: JSX.Element;
  }) => ReactNode;
};

const membersColumnHelper = createMRTColumnHelper<AdministratorInvitation>();

export default function AdministratorInvitationsTable(props: Readonly<AdministratorInvitationsTableProps>) {
  const { renderTopToolbarCustomActions } = props;

  // State
  const [query, setQuery] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Hooks
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Queries
  const {
    refetch: getAdminInvitations,
    data: legalEntityAdminInvitations,
    isLoading,
    isFetching,
    error
  } = useGetAdminInvitationsForLegalEntityQuery(
    session.user!.legalEntityId,
    {
      skip: sessionPermissions === undefined
    }
  );
  
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if(!sessionPermissions?.includesAccessToAdministration) return;
    getAdminInvitations();
  }, [sessionPermissions, getAdminInvitations]);

  const data = React.useMemo(() => {
    if(!legalEntityAdminInvitations) return [];
    return legalEntityAdminInvitations
      .map(i => AdministratorInvitation.fromJSON(i))
      .filter(i => i != null);
  }, [legalEntityAdminInvitations]);

  const renderNameColumn = React.useCallback((data: AdministratorInvitation): React.ReactNode => {
    const invitation: AdministratorInvitation = data;
    const name = `${invitation.registrationInfo?.name?.firstName} ${invitation.registrationInfo?.name?.lastName}`;
    const email = invitation.registrationInfo?.email?.value;
    const contactLink = <RowTitle>{name}</RowTitle>;

    return (
      <NameCellContainer>
        <NameText primary={contactLink} secondary={email} />
      </NameCellContainer>
    );
  }, []);

  const renderStatusColumn = React.useCallback((data: AdministratorInvitation): React.ReactNode => {
    return data?.status?.valueOf() ?? "Unknown";
  }, []);

  const columns = React.useMemo(() => [
    membersColumnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    membersColumnHelper.accessor(renderStatusColumn, {
      header: "Status",
      id: "status",
    }),
  ], [renderNameColumn, renderStatusColumn]);

  const tableOptions: MRT_TableOptions<AdministratorInvitation> = {
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button startIcon={<PersonAddIcon />} onClick={() => navigate(`/administration/invitations/invite`)}>
              Invite Users
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate(`/administration/invitations/invite`)}>
            <PersonAddIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: error
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: data.length,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: !!error,
      showProgressBars: isFetching,
      sorting,
    },
  }

  return <MRTNoMemo<AdministratorInvitation> options={tableOptions} />;
}
